import { AlertTriangle, Clock3, IndianRupee, Package, PackageX, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDashboard } from '@/api/hooks/useDashboard';
import { useSession } from '@/auth/session';
import { AlertsList } from '@/features/dashboard/AlertsList';
import { RevenueSparkline } from '@/features/dashboard/RevenueSparkline';
import { StatTile } from '@/features/dashboard/StatTile';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { money, moneyCompact } from '@/lib/utils';

function TrendBadge({ trend }: { trend?: number | null }) {
  if (trend === null || trend === undefined) return null;
  const positive = trend >= 0;
  // Overrides the semantic ok/out colors — green-on-orange (or red-on-orange)
  // clashes against this card's coral gradient, so this badge always uses a
  // translucent white pill instead, matching the mobile app's hero badge.
  return (
    <Badge variant="outline" className="tabular border-white/30 bg-white/20 text-white">
      {positive ? '+' : ''}
      {trend}% vs yesterday
    </Badge>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, pharmacy } = useSession();
  const { data, isLoading, isError } = useDashboard();

  if (isLoading) return <DashboardSkeleton />;

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load the dashboard. Pull to refresh, or try again shortly.
        </CardContent>
      </Card>
    );
  }

  const { stats, todaySales, sparkline, alerts, subscription } = data;
  const greetingName = user?.name.split(' ')[0] ?? 'there';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">Hey, {greetingName}</h1>
          <p className="text-sm text-muted-foreground">
            {pharmacy?.name ?? t('appName')} ·{' '}
            {subscription.plan === 'Trial' && subscription.daysRemaining != null
              ? `${subscription.daysRemaining} day(s) left in trial`
              : subscription.plan}
          </p>
        </div>
      </div>

      {/* Today's takings — the number the pharmacist actually opened the app for. */}
      <Card className="overflow-hidden border-none bg-grad-primary text-white">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-white/70">{t('todaySales')}</p>
            <p className="tabular font-display text-4xl">{money(todaySales.total)}</p>
            <p className="mt-1 text-sm text-white/70">{todaySales.count} bill(s)</p>
          </div>
          <TrendBadge trend={todaySales.trend} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label={t('totalMedicines')} value={stats.totalMedicines} icon={Package} to="/inventory" tone="mint" />
        <StatTile
          label={t('outOfStock')}
          value={stats.outOfStock}
          icon={PackageX}
          to="/inventory?status=out"
          tone={stats.outOfStock > 0 ? 'rose' : 'mint'}
        />
        <StatTile
          label={t('lowStock')}
          value={stats.lowStock}
          icon={AlertTriangle}
          to="/inventory?status=low"
          tone={stats.lowStock > 0 ? 'amber' : 'mint'}
        />
        <StatTile
          label={t('expiringSoon')}
          value={stats.expiringSoon}
          icon={Clock3}
          to="/inventory?status=expiring"
          tone="sky"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Last 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            <RevenueSparkline data={sparkline} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Needs attention</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <AlertsList alerts={alerts} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <StatTile label={t('navSuppliers')} value={stats.totalSuppliers} icon={Truck} to="/suppliers" tone="grape" />
        <StatTile
          label="Inventory value (retail)"
          value={moneyCompact(stats.retailValue)}
          icon={IndianRupee}
          to="/reports"
          tone="mint"
        />
      </div>
    </div>
  );
}
